// dlc-view.ts — the HTML/DOM ADD-ON MISSION PACKS flow, VIEW half (HTML/DOM UI
// shell plan, Phase 3). Renders DlcController state to the DOM and routes
// clicks back to its actions; browser-only (the logic + HTTP boundary live in
// dlc-controller.ts, which is node-tested against a fake HttpService). FAITHFUL
// chrome — the SAME treatment the SP menu (menu-view.ts) and the MP lobby
// (lobby-view.ts) got: every screen is a 240×160 forms.ini design stage
// (formScale/makeStage/at), composited 9-slice buttons placed at authored
// coords, the zip-in cascade on a screen change, the solid steel-blue list
// selection bar. The three forms (forms.ini):
//   chooser  [kidfAddOnSingleMulti] — SINGLE PLAYER / MULTIPLAYER MISSION PACKS
//   catalog  [kidfDownloadMissionPackWide] — the multi-column pack LIST + info
//            label + DOWNLOAD/DISCUSS/DONE
//   download [kidfDownloadBox 190×70] — the modal download-progress box overlaid
//            on the catalog stage (OK / PLAY)
// On done() the host swaps back to the menu (like startDomLobby).
//
// The composited 9-slice button (button/composite) is now the SHARED chrome.ts
// makeShellButton factory (de-dup of the menu/lobby/dlc copies). The remaining
// makeStage/at/zipIn/listItem helpers stay local to each faithful screen-set.

import type { DlcController } from './dlc-controller.ts';
import { type ButtonArt } from './title-art.ts';
import { ksfxGuiButtonTap, ksfxGuiScrollingListSelectItem } from '../ui/uikit.ts';
import { ksfxGuiFormShow } from '../ui/uikit.ts';
import { zipInStage } from './formchrome.ts';
import {
  COL_BG,
  COL_FG,
  TEXT_SHADOW,
  el,
  formScale,
  FONT_BODY,
  buttonWidthU,
  makeShellButton,
  formTitle,
  ensureA11yStyles,
  stageTextInput,
  prefersReducedMotion,
  centeredBand,
  isPortraitBoost,
  mountViewportFit,
  type ViewportFit,
  withFocusPreserved,
  autofocusOnce,
  type AutofocusOnce,
} from './chrome.ts';

export interface DlcViewDeps {
  controller: DlcController;
  root: HTMLElement;
  showCanvas: () => void;
  hideCanvas: () => void;
  /** the title-screen backdrop (titlescreenbkgd.png) as a CSS url. */
  titleBg?: string;
  /** the raw 9-slice button pieces — the faithful chrome shared with the SP
   *  menu + MP lobby; absent ⇒ a flat CSS-button fallback. */
  buttonArt?: ButtonArt;
  /** play a GUI sound effect (ksfxGui*) on press, wired to the host SoundMgr. */
  playSfx?: (sfx: number) => void;
}

const FONT = FONT_BODY;
const shadow = TEXT_SHADOW;

export class DlcView {
  private readonly d: DlcViewDeps;
  /** the last screen rendered — the zip-in plays only on a screen CHANGE. */
  private lastScreen: string | null = null;
  /** the last MODAL overlay shown ('download' | 'remove' | 'customurl' | null) — the
   *  box zip-in plays only when a modal NEWLY appears, not on every progress re-render. */
  private lastModal: string | null = null;
  /** pending "form shown" SFX (fires at zip end; cleared on a screen change). */
  private formShowTimer: ReturnType<typeof setTimeout> | null = null;
  /** pending modal-box "form shown" SFX (separate timer so it isn't cancelled by the
   *  stage zip's timer). */
  private modalShowTimer: ReturnType<typeof setTimeout> | null = null;
  /** keeps the form clear of the soft keyboard (chrome.mountViewportFit). */
  private readonly viewport: ViewportFit;
  private readonly urlAutofocus: AutofocusOnce = autofocusOnce();
  /** the modal overlay currently mounted, if any — fitted to the keyboard band alongside the
   *  root (it is position:fixed, so the root's overflow:hidden doesn't clip it). */
  private modalOverlay: HTMLElement | null = null;
  /** the shared 9-slice shell ButtonControl factory (chrome.ts) — its own composite
   *  cache + up/down state swap + ksfxGuiButtonTap. */
  private readonly btn: (
    label: string,
    onClick: () => void,
    enabled?: boolean,
    size?: { w: number; h: number },
  ) => HTMLButtonElement;

  constructor(d: DlcViewDeps) {
    this.d = d;
    this.btn = makeShellButton(d, 'sub');
    ensureA11yStyles(); // the global keyboard :focus-visible ring (once)
    const s = d.root.style;
    s.position = 'fixed';
    s.inset = '0';
    s.display = 'flex';
    s.flexDirection = 'column';
    s.alignItems = 'center';
    s.justifyContent = 'center';
    s.background = COL_BG;
    s.color = COL_FG;
    s.fontFamily = FONT;
    s.userSelect = 'none';
    s.zIndex = '10';
    s.overflow = 'hidden'; // clip controls parked off-window during the shared zip (no scrollbar flash)
    // A height-only resize is the soft keyboard, and re-rendering there would
    // replaceChildren() away the focused custom-URL field, dismissing the keyboard. Only a
    // WIDTH change is a real orientation/window change worth rebuilding for. See chrome.ts.
    this.viewport = mountViewportFit(d.root, {
      rebuild: () => this.render(),
      // the modal overlays are fixed (so the root's overflow:hidden can't clip them), which
      // means they need narrowing to the band themselves
      alsoFit: () => this.modalOverlay,
    });
    window.addEventListener('keydown', this.onKeyDown);
    this.render();
  }

  // Escape → the current screen's BACK action (the SAME thing a BACK/DONE/CANCEL
  // click does), matching the in-game dialogs' Esc wiring (confirm-view.ts). An open
  // modal (download / remove-confirm / custom-URL) takes Escape FIRST (its CANCEL).
  private readonly onKeyDown = (e: KeyboardEvent): void => {
    if (e.key !== 'Escape') return;
    const c = this.d.controller;
    e.preventDefault();
    if (c.pendingRemove !== null) {
      c.cancelRemove();
      return;
    }
    if (c.pendingCustomUrl) {
      c.cancelCustomUrl();
      return;
    }
    if (c.screen === 'download') {
      c.downloadOk();
      return;
    } // the box's OK/CANCEL
    c.done(); // chooser / catalog BACK → end the flow (done(null) → the menu)
  };

  /** detach the listeners (a re-created view must not leak ones bound to the stale
   *  instance). */
  destroy(): void {
    this.viewport.detach();
    this.urlAutofocus.cancel();
    window.removeEventListener('keydown', this.onKeyDown);
    if (this.formShowTimer !== null) clearTimeout(this.formShowTimer);
    if (this.modalShowTimer !== null) clearTimeout(this.modalShowTimer);
  }

  render(): void {
    const c = this.d.controller,
      root = this.d.root;
    this.d.hideCanvas();
    root.style.display = 'flex';
    // EVERY screen renders over the title backdrop, like the canvas ShellForm
    // (shellform.ts OnPaintBackground draws titlescreenbkgd.png with a letterbox).
    root.style.backgroundImage = this.d.titleBg !== undefined ? `url(${this.d.titleBg})` : 'none';
    root.style.backgroundColor = COL_BG;
    root.style.backgroundSize = 'cover';
    root.style.backgroundPosition = 'center';
    root.style.backgroundRepeat = 'no-repeat';

    let screen: HTMLElement;
    switch (c.screen) {
      case 'catalog':
        screen = this.catalogScreen();
        break;
      case 'download':
        screen = this.catalogScreen();
        break; // the box overlays the catalog stage
      default:
        screen = this.chooserScreen();
    }

    // Selecting a catalog row re-renders the whole screen, which rebuilds the
    // scrolling pack list fresh (scrollTop 0) and snaps it back to the top,
    // hiding the just-tapped row. On a same-screen re-render carry the old
    // scroll position over (lists keep their order across a re-render, so match
    // them by position); on a screen CHANGE the list set differs, so let it
    // reset. The SAME fix the SP menu (menu-view) + MP lobby (lobby-view) carry.
    const changed = c.screen !== this.lastScreen;
    const prevScroll = changed
      ? []
      : Array.from(root.querySelectorAll<HTMLElement>('[data-list]')).map((e) => e.scrollTop);
    // catalog-stage modal overlays (the download box, the canvas HtMessageBox query, the
    // custom-URL ask). These are full-window position:fixed overlays — appended to ROOT
    // (a sibling of the stage), NOT to the stage, so the stage zip (zipInStage) only
    // cascades the FORM controls and never drags a modal in from the edge. The modal's
    // OWN box zips separately (zipInModal) so the download / remove / custom-URL dialogs
    // animate in like every other screen (the user asked the download screen to zipper).
    let overlayBox: HTMLElement | null = null; // the dialog box to zip (if a modal is up)
    let modal: string | null = null; // which modal (drives the once-per-open zip)
    this.modalOverlay = null; // re-captured below if one mounts
    // The rebuild spans the modal appends too: the custom-URL <input> lives in one of them, and
    // replaceChildren would otherwise blur it and drop the soft keyboard. (Typing itself does
    // NOT re-render — dlc-controller's setCustomUrl doesn't notify — but a download-progress
    // push while the ask is up does.)
    withFocusPreserved(root, () => {
      root.replaceChildren(screen);
      // a FRESH stage carries none of the keyboard fit, so re-apply it BEFORE the scroll
      // restore below (a scrollTop write forces layout — do it at the final size)
      this.viewport.fit();
      if (!changed) {
        const lists = screen.querySelectorAll<HTMLElement>('[data-list]');
        lists.forEach((e, i) => {
          if (prevScroll[i] !== undefined) e.scrollTop = prevScroll[i];
        });
      }
      if (c.screen === 'download') {
        this.modalOverlay = this.downloadBox();
        modal = 'download';
      } else if (c.pendingRemove !== null) {
        this.modalOverlay = this.confirmRemoveBox();
        modal = 'remove';
      } else if (c.pendingCustomUrl) {
        this.modalOverlay = this.customUrlBox();
        modal = 'customurl';
      }
      if (this.modalOverlay !== null) {
        root.append(this.modalOverlay);
        overlayBox = this.modalOverlay.firstElementChild as HTMLElement;
        this.viewport.fit(); // narrow the freshly-mounted overlay to the band too
      }
    });
    // the ask closed → the next open counts as a fresh one (its key repeats, so unlike the
    // lobby's per-ask prompt object this one has to be reset explicitly)
    if (!c.pendingCustomUrl) this.urlAutofocus.reset();

    // the stage zip plays on a screen change; the download box overlay opens on the
    // SAME catalog stage, so treat 'download' as its own screen for the stage zip
    // (`changed` was computed above, before the scroll-preserving replaceChildren).
    this.lastScreen = c.screen;
    if (changed) this.zipIn(screen);

    // the modal box zips in when it NEWLY appears (not on a progress re-render — the
    // download box re-renders as the bytes arrive, and re-zipping each time would jitter).
    const modalChanged = modal !== this.lastModal;
    this.lastModal = modal;
    if (modal !== null && modalChanged && overlayBox !== null) this.zipInModal(overlayBox);
  }

  // ── the faithful design stage (forms.ini: the shell forms are 240×160) ───────
  // Copied from lobby-view.ts (the verified-faithful chrome): a FIXED
  // magnification (≈2 game px per design unit × gameScale), centered, shrinking
  // only when a 240×160 form (+margin) won't fit a small window.
  private makeStage(wUnits = 240): { stage: HTMLElement; s: number } {
    const s = formScale();
    const stage = el('div', {
      position: 'absolute',
      left: '50%',
      top: '50%',
      width: `${wUnits * s}px`,
      height: `${160 * s}px`,
      transform: 'translate(-50%, -50%)',
    });
    return { stage, s };
  }

  private at(
    child: HTMLElement,
    s: number,
    x: number,
    y: number,
    w?: number,
    h?: number,
  ): HTMLElement {
    Object.assign(child.style, { position: 'absolute', left: `${x * s}px`, top: `${y * s}px` });
    if (w !== undefined) child.style.width = `${w * s}px`;
    if (h !== undefined) child.style.height = `${h * s}px`;
    return child;
  }

  private stageButton(
    s: number,
    label: string,
    x: number,
    y: number,
    w: number,
    h: number,
    on: () => void,
    en = true,
  ): HTMLElement {
    return this.at(this.btn(label, on, en, { w: w * s, h: h * s }), s, x, y);
  }

  // a centred modal-box BUTTON ROW (the download / remove / custom-URL dialogs): each button
  // SIZED to its label via the shared buttonWidthU then centred across the `boxW`-wide box with
  // an even gap, so the WI-font label keeps clear margin inside the 9-slice frame.
  private modalButtonRow(
    box: HTMLElement,
    s: number,
    y: number,
    h: number,
    boxW: number,
    btns: { label: string; on: () => void }[],
  ): void {
    const gapU = 8;
    const sized = btns.map((b) => ({ ...b, w: buttonWidthU(b.label, h, s) }));
    const totalW = sized.reduce((a, b) => a + b.w, 0) + gapU * (sized.length - 1);
    let x = Math.max(0, (boxW - totalW) / 2);
    for (const b of sized) {
      box.append(this.stageButton(s, b.label, x, y, b.w, h, b.on));
      x += b.w + gapU;
    }
  }

  // the ShellForm zip-in — now the SHARED zipInStage (formchrome.ts), the SAME cascade
  // the SP menu (menu-view) and MP lobby (lobby-view) use, so the Mission Packs screens
  // no longer drift (they used to slide only buttons by ±130% WITH an opacity fade, while
  // every other shell screen cascades ALL top-level controls from the window edge with no
  // fade — a per-view divergence this de-dups). zipInStage zips the stage's DIRECT children
  // (the list box is one child → it zips as a unit; its rows don't cascade); the modal
  // overlays live on root, not the stage, so they're untouched. The reduced-motion guard
  // + the ksfxGuiFormShow timing now come from the shared helper.
  private zipIn(screen: HTMLElement): void {
    if (this.formShowTimer !== null) clearTimeout(this.formShowTimer);
    this.formShowTimer = zipInStage(screen, this.d.playSfx);
  }

  // the MODAL-box zip-in (the download / remove / custom-URL dialog). The box's controls
  // cascade in from alternating sides of the BOX (offset = the box's own width, so they
  // sweep in tightly rather than flying across the whole window like the full-form zip),
  // staggered like the ShellForm zip, then ksfxGuiFormShow plays. The box clips during the
  // slide so the off-box start positions don't spill past the dim. Honours reduced motion.
  private zipInModal(box: HTMLElement): void {
    if (this.modalShowTimer !== null) clearTimeout(this.modalShowTimer);
    const els = [...box.children] as HTMLElement[];
    if (prefersReducedMotion()) {
      this.modalShowTimer = setTimeout(() => {
        this.modalShowTimer = null;
        this.d.playSfx?.(ksfxGuiFormShow);
      }, 0);
      return;
    }
    const off = box.getBoundingClientRect().width || 240; // slide in from just off the box edge
    const prevOverflow = box.style.overflow;
    box.style.overflow = 'hidden'; // clip the off-box start positions during the slide
    els.forEach((e, i) => {
      e.style.transform = `translateX(${i % 2 === 0 ? off : -off}px)`;
    });
    void box.offsetWidth; // commit the start positions before transitioning
    els.forEach((e, i) => {
      e.style.transition = `transform 250ms linear ${i * 30}ms`;
      e.style.transform = 'translateX(0)';
    });
    this.modalShowTimer = setTimeout(
      () => {
        this.modalShowTimer = null;
        box.style.overflow = prevOverflow; // restore so nothing inside is clipped post-zip
        this.d.playSfx?.(ksfxGuiFormShow);
      },
      250 + els.length * 30,
    );
  }

  // ── chooser (kidfAddOnSingleMulti) ───────────────────────────────────────────
  // title "ADD-ON MISSION PACKS" (2,6,236) center; canvas BUTTONs read "SINGLE PLAYER
  // MISSION PACKS" (55,42,120,14) / "MULTIPLAYER MISSION PACKS" (55,59,120,14); BUTTON
  // BACK (130,140,40,14). The DOM labels are SHORTENED to "SINGLE PLAYER" / "MULTIPLAYER"
  // by USER REQUEST (the title already says "…MISSION PACKS", so the long button text was
  // redundant) — an intentional content change, not a layout artifact.
  private chooserScreen(): HTMLElement {
    const c = this.d.controller,
      { stage, s } = this.makeStage();
    stage.append(this.at(formTitle('ADD-ON MISSION PACKS', s), s, 2, 6, 236));
    // the two stacked chooser buttons (SINGLE PLAYER / MULTIPLAYER — user-shortened, see
    // above) — a DELIBERATELY consistent-width pair: ONE uniform width = the widest label
    // ("SINGLE PLAYER", via the shared cap-aware buttonWidthU), centred on the 240-form, so
    // they read as a clean column AND match the Play Game screen's same SP/MP pair.
    const wSpMp = Math.max(
      buttonWidthU('SINGLE PLAYER', 14, s),
      buttonWidthU('MULTIPLAYER', 14, s),
    );
    stage.append(
      this.stageButton(s, 'SINGLE PLAYER', (240 - wSpMp) / 2, 42, wSpMp, 14, () =>
        c.chooseSinglePlayer(),
      ),
    );
    stage.append(
      this.stageButton(s, 'MULTIPLAYER', (240 - wSpMp) / 2, 59, wSpMp, 14, () =>
        c.chooseMultiplayer(),
      ),
    );
    const wBack = buttonWidthU('BACK', 14, s);
    stage.append(this.stageButton(s, 'BACK', (240 - wBack) / 2, 140, wBack, 14, () => c.done()));
    return stage;
  }

  // ── catalog (kidfDownloadMissionPackWide) ────────────────────────────────────
  // title "DOWNLOAD ADD-ON MISSION PACKS" (0,6,240) center; column headers at
  // y=16 (Status/Title/# Players/# Missions); multi-column LIST (2,24,236,86);
  // info LABEL (2,112,236,25) multiline; BUTTON DOWNLOAD (40,141,55,12), DISCUSS
  // (103,141,48,12), DONE (159,141,40,12). DOWNLOAD hidden when nothing
  // downloadable is selected; DISCUSS opens the pack's forum URL.
  private catalogScreen(): HTMLElement {
    const c = this.d.controller,
      { stage, s } = this.makeStage();
    const rows = c.rows();

    stage.append(this.at(formTitle('DOWNLOAD ADD-ON MISSION PACKS', s), s, 0, 6, 240));

    // the column header labels (dlmissionpack.cpp PositionColumns) over the list.
    // Tappable — they sort the catalog by that column. The active sort column
    // carries a trailing '*' (dlmissionpack.cpp:304-321).
    const tapHdr = (col: 'status' | 'title' | 'players' | 'missions') => () => {
      this.d.playSfx?.(ksfxGuiButtonTap);
      c.sortBy(col);
    };
    const active = c.sortColumn();
    const mark = (col: 'status' | 'title' | 'players' | 'missions', text: string): string =>
      col === active ? `${text}*` : text;
    // portrait: the four wide columns (x≈4..238) won't fit the 160u band, so under the boost
    // narrow them (shorter "P"/"M" labels) and pack across the band — every column keeps its
    // sort tap target; landscape keeps the authored four full-width headers.
    if (isPortraitBoost()) {
      const b = centeredBand(2, 236); // {40, 160}
      stage.append(
        this.at(
          this.colHeader(mark('status', 'S'), 'left', s, tapHdr('status')),
          s,
          b.x + 2,
          16,
          12,
        ),
      );
      stage.append(
        this.at(
          this.colHeader(mark('title', 'Title'), 'left', s, tapHdr('title')),
          s,
          b.x + 16,
          16,
          86,
        ),
      );
      stage.append(
        this.at(
          this.colHeader(mark('players', 'P'), 'center', s, tapHdr('players')),
          s,
          b.x + 104,
          16,
          20,
        ),
      );
      stage.append(
        this.at(
          this.colHeader(mark('missions', 'M'), 'right', s, tapHdr('missions')),
          s,
          b.x + 126,
          16,
          32,
        ),
      );
    } else {
      stage.append(
        this.at(this.colHeader(mark('status', 'S'), 'left', s, tapHdr('status')), s, 4, 16, 14),
      );
      stage.append(
        this.at(this.colHeader(mark('title', 'Title'), 'left', s, tapHdr('title')), s, 20, 16, 130),
      );
      stage.append(
        this.at(
          this.colHeader(mark('players', '# Players'), 'center', s, tapHdr('players')),
          s,
          150,
          16,
          46,
        ),
      );
      stage.append(
        this.at(
          this.colHeader(mark('missions', '# Missions'), 'right', s, tapHdr('missions')),
          s,
          196,
          16,
          42,
        ),
      );
    }

    // the multi-column pack list (2,24,236,86) — selectable rows, double-click
    // triggers the action (download/remove). portrait: reflow into the centered band.
    const listB = centeredBand(2, 236); // {40, 160} in portrait, {2, 236} otherwise
    const empty = c.catalogStatus !== '' ? c.catalogStatus : 'No mission packs available.';
    stage.append(
      this.at(
        this.listBox(
          rows.map((r) => ({
            key: r.key,
            html: this.packRow(r, s, isPortraitBoost()),
            selected: c.isSelected(r.key),
            onSelect: () => {
              c.select(r.key);
            },
            onActivate: () => {
              c.select(r.key);
              if (c.actionLabel() !== null) c.action();
            },
          })),
          empty,
          s,
        ),
        s,
        listB.x,
        24,
        listB.w,
        86,
      ),
    );

    // the info label (2,112,236,25) — the selected pack's "By <author>: <desc>".
    // portrait: reflow into the centered band.
    const infoB = centeredBand(2, 236);
    stage.append(this.at(this.infoLabel(c.infoLabel, s), s, infoB.x, 112, infoB.w, 25));

    // the bottom action row (DOWNLOAD/REMOVE/UPGRADE · DISCUSS · DONE) — each button SIZED
    // to its label via the shared buttonWidthU (the wide WI face overran the fixed 66/54u
    // boxes so "DOWNLOAD"/"DISCUSS" touched the 9-slice frame) then packed as a RIGHT-anchored
    // cluster with even gaps so DONE keeps its canvas right-edge position (kidcCancel) and the
    // optional DOWNLOAD/DISCUSS sit to its left. Filtered to the shown buttons.
    const action = c.actionLabel();
    const rowBtns: { label: string; on: () => void }[] = [];
    if (action !== null) rowBtns.push({ label: action, on: () => c.action() });
    if (c.canDiscuss()) rowBtns.push({ label: 'DISCUSS', on: () => c.discuss() });
    if (c.canReport()) rowBtns.push({ label: 'REPORT', on: () => c.reportPack() });
    rowBtns.push({ label: 'DONE', on: () => c.done() }); // always shown; the right-most button
    // landscape: a RIGHT-anchored cluster (DONE keeps its canvas right edge at 211u, the
    // optional DOWNLOAD/DISCUSS to its left). PORTRAIT: the auto-sized buttons can total MORE
    // than the centered band, so CENTER the row in the band and shrink it to ONE line if needed
    // (h drives the 9-slice label font, so the widths shrink with it — no clipped labels).
    const gapU = 6;
    let h = 12;
    let sized = rowBtns.map((b) => ({ ...b, w: buttonWidthU(b.label, h, s) }));
    let totalW = sized.reduce((a, b) => a + b.w, 0) + gapU * (sized.length - 1);
    let x: number;
    if (isPortraitBoost()) {
      const band = centeredBand(2, 236); // {40,160}
      if (totalW > band.w) {
        // shrink the whole row to fit one line in the band
        const f = band.w / totalW;
        h *= f;
        sized = sized.map((b) => ({ ...b, w: b.w * f }));
        totalW = band.w;
      }
      x = band.x + (band.w - totalW) / 2; // centered in the band
    } else {
      x = Math.max(2, 211 - totalW); // the canvas right anchor (DONE's right edge at 211u)
    }
    for (const b of sized) {
      stage.append(this.stageButton(s, b.label, x, 141, b.w, h, b.on));
      x += b.w + gapU;
    }
    return stage;
  }

  // a catalog list row: status marker / title / # players / # missions laid out
  // in columns aligned with the headers (the canvas tab-stopped ListControl item).
  // PORTRAIT: the authored 12/132/44/40u columns total 228u, which overflows the 160u
  // centered band the list reflows into — the #Players/#Missions cells were pushed right
  // off-screen, so the row no longer "satisfied" its band-narrowed headers. Use the
  // narrowed column SPANS (12/88/22/32 = 154u) that line up under the portrait headers
  // (S@2 / Title@16 / P@104 / M@126 within the band; see catalogScreen).
  private packRow(
    r: { status: string; title: string; players: string; missions: string; custom: boolean },
    s: number,
    portrait: boolean,
  ): HTMLElement {
    const row = el('div', {
      display: 'flex',
      alignItems: 'center',
      width: '100%',
      fontSize: `${Math.round(5 * s)}px`,
    });
    const cell = (text: string, w: number, align: 'left' | 'center' | 'right'): HTMLElement => {
      const e = el('div', {
        width: `${w * s}px`,
        flex: '0 0 auto',
        textAlign: align,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      });
      e.textContent = text;
      return e;
    };
    const [wS, wT, wP, wM] = portrait ? [12, 88, 22, 32] : [12, 132, 44, 40];
    row.append(
      cell(r.status, wS, 'center'),
      cell(r.title, wT, 'left'),
      cell(r.players, wP, 'center'),
      cell(r.missions, wM, 'right'),
    );
    return row;
  }

  // a dimmed full-screen overlay holding a centered design-stage dialog box (a
  // solid steel-blue title bar + white border + dark fill — the canvas
  // DoModal SetTitleColor/Border/Background). Shared by the download box, the
  // REMOVE confirm, and the Custom URL ask. [data-list] so it isn't form-zipped.
  private modalBox(
    title: string,
    w: number,
    h: number,
  ): { overlay: HTMLElement; box: HTMLElement; s: number } {
    const overlay = el('div', {
      // FIXED, and narrowed to the keyboard band by mountViewportFit's `alsoFit` (see the
      // constructor). Absolute would clip it against the root's `overflow:hidden` once the
      // root shrinks — losing the OK / CANCEL row — while plain fixed would centre the box,
      // custom-URL field included, behind the keyboard. Fixed + fitted is both.
      position: 'fixed',
      inset: '0',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'rgba(0,0,0,0.45)',
      zIndex: '20',
    });
    overlay.dataset.list = '1';
    // portrait: the flex-centered box isn't on the 240u stage, so a 190u box at the boosted
    // scale (≈448px) overflows a ~390px phone. Shrink the WHOLE modal scale (box + content)
    // so the box fits the viewport. Capping only the box CSS width — the previous approach —
    // left the content placed at the un-capped scale (6u..184u), so it spilled past the right
    // border and clipped the message/status text (the reported layout bug).
    const s = isPortraitBoost() ? Math.min(formScale(), (window.innerWidth - 16) / w) : formScale();
    const box = el('div', {
      position: 'relative',
      width: `${w * s}px`,
      height: `${h * s}px`,
      background: 'rgba(7,22,27,0.96)',
      border: `${Math.max(1, Math.round(s))}px solid #fff`,
      boxShadow: '0 6px 24px rgba(0,0,0,0.6)',
    });
    const bar = el('div', {
      position: 'absolute',
      left: '0',
      right: '0',
      top: '0',
      height: `${12 * s}px`,
      background: 'rgb(60, 96, 150)',
      color: '#fff',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontFamily: FONT,
      fontSize: `${Math.round(6 * s)}px`,
      fontWeight: '700',
      letterSpacing: '0.06em',
    });
    bar.textContent = title;
    box.append(bar);
    overlay.append(box);
    return { overlay, box, s };
  }

  // ── the modal DownloadBox (kidfDownloadBox 190×70) ───────────────────────────
  // title "Download Mission Pack" center; Message (6,15,178) center multiline;
  // Status (6,33,178) center multiline; OK (36,52,54,12); PLAY (100,52,54,12).
  // While downloading only OK ("CANCEL"); on completion both OK + PLAY. Rendered
  // as a dimmed overlay on the catalog stage (like lobby-view's overlays).
  private downloadBox(): HTMLElement {
    const c = this.d.controller;
    const { overlay, box, s } = this.modalBox('Download Mission Pack', 190, 70);
    // Message (the "Downloading <pack>" line)
    box.append(this.at(this.centerText(c.downloadTitle, s), s, 6, 15, 178, 14));
    // Status (progress / completion / error)
    box.append(this.at(this.centerText(c.downloadStatus, s), s, 6, 33, 178, 14));

    if (c.downloadDone) {
      // OK + PLAY (PLAY only on a successful, non-error completion) — sized to label, centred.
      this.modalButtonRow(
        box,
        s,
        52,
        12,
        190,
        c.downloadError
          ? [{ label: 'OK', on: () => c.downloadOk() }]
          : [
              { label: 'OK', on: () => c.downloadOk() },
              { label: 'PLAY', on: () => c.downloadPlay() },
            ],
      );
    } else {
      // CANCEL only (centered) — downloadbox.cpp centers OK→"CANCEL" while busy
      this.modalButtonRow(box, s, 52, 12, 190, [{ label: 'CANCEL', on: () => c.downloadOk() }]);
    }
    return overlay;
  }

  // ── the REMOVE confirm (the canvas HtMessageBox 'Are you sure?' query) ───────
  // dlmissionpack.cpp:649-659 — every REMOVE is gated by a Yes/No query box
  // titled "Remove Mission Pack". YES uninstalls; NO dismisses.
  private confirmRemoveBox(): HTMLElement {
    const c = this.d.controller;
    const { overlay, box, s } = this.modalBox('Remove Mission Pack', 190, 70);
    box.append(this.at(this.centerText('Are you sure?', s), s, 6, 22, 178, 14));
    this.modalButtonRow(box, s, 52, 12, 190, [
      { label: 'YES', on: () => c.confirmRemove() },
      { label: 'NO', on: () => c.cancelRemove() },
    ]);
    return overlay;
  }

  // ── the Custom URL ask (dlmissionpack.cpp:715 HandleCustomURL → InitiateAsk) ──
  // a text-entry box for the custom pack URL (the canvas window/keyboard ask).
  // window.prompt is NOT used — it throws in a cross-origin iframe — so this is a
  // DOM input overlay on the design stage, like the lobby's room/password asks.
  private customUrlBox(): HTMLElement {
    const c = this.d.controller;
    const { overlay, box, s } = this.modalBox('Custom URL', 190, 70);
    box.append(this.at(this.centerText('Enter the mission pack URL:', s), s, 6, 16, 178, 12));
    const input = this.textInput('dlcUrl', 'https://…', c.customUrl, s);
    const submit = (): void => {
      this.d.playSfx?.(ksfxGuiButtonTap);
      c.submitCustomUrl();
    };
    input.addEventListener('input', () => {
      c.setCustomUrl(input.value);
    });
    input.addEventListener('keydown', (e: KeyboardEvent) => {
      if (e.key === 'Enter') submit();
    });
    box.append(this.at(this.fieldWrap(input), s, 6, 30, 178, 14));
    this.modalButtonRow(box, s, 52, 12, 190, [
      { label: 'OK', on: submit },
      { label: 'CANCEL', on: () => c.cancelCustomUrl() },
    ]);
    // focus the input so the user can type immediately — but ONCE per open, not on every
    // rebuild of this box (that re-stole focus and snapped the caret to the end)
    this.urlAutofocus.apply('open', input);
    return overlay;
  }

  // ── widgets (copied from lobby-view.ts — the faithful chrome) ────────────────
  private centerText(text: string, s: number): HTMLElement {
    const e = el('div', {
      ...labelStyle(s),
      textAlign: 'center',
      opacity: '0.95',
      lineHeight: '1.2',
    });
    e.textContent = text;
    return e;
  }

  // a text-entry field (the custom-URL ask) — same chrome as the lobby asks. Was a
  // pre-PR-#242 copy of the field (no numeric line-height), so on Safari/WebKit its
  // value AND placeholder sat low; delegate to the shared helper so it can't drift.
  private textInput(
    field: string,
    placeholder: string,
    value: string,
    s: number,
  ): HTMLInputElement {
    const input = stageTextInput(field, placeholder, value, s);
    input.type = 'text';
    input.autocomplete = 'off';
    input.spellcheck = false;
    return input;
  }

  // wrap an input so at(...) can size+position it (the input fills the wrapper).
  private fieldWrap(input: HTMLInputElement): HTMLElement {
    const w = el('div', { width: '100%', height: '100%' });
    w.append(input);
    return w;
  }

  // the multi-line info label (the DownloadMissionPackForm kidcMissionPackInfo).
  private infoLabel(text: string, s: number): HTMLElement {
    const e = el('div', {
      ...labelStyle(s),
      opacity: '0.9',
      lineHeight: '1.25',
      overflow: 'hidden',
      display: '-webkit-box',
      WebkitBoxOrient: 'vertical',
      WebkitLineClamp: '4',
    } as Partial<CSSStyleDeclaration>);
    e.textContent = text;
    return e;
  }

  // a catalog column header (dlmissionpack.cpp PositionColumns labels) — tappable
  // to sort by that column (the canvas header-tap sort).
  private colHeader(
    text: string,
    align: 'left' | 'center' | 'right',
    s: number,
    onTap: () => void,
  ): HTMLElement {
    const e = el('div', {
      ...labelStyle(s),
      textAlign: align,
      opacity: '0.8',
      fontWeight: '700',
      whiteSpace: 'nowrap',
      cursor: 'pointer',
    });
    e.textContent = text;
    e.addEventListener('click', onTap);
    return e;
  }

  // a fixed-size scrolling list control: selectable rows (the solid steel-blue
  // highlight bar), an empty-state message centered inside the SAME box. The box
  // carries [data-list] so its rows are excluded from the form zip-in.
  private listBox(
    rows: {
      key: string;
      html: HTMLElement;
      selected: boolean;
      onSelect: () => void;
      onActivate: () => void;
    }[],
    emptyMsg: string,
    s: number,
  ): HTMLElement {
    const box = el('div', {
      display: 'flex',
      flexDirection: 'column',
      gap: '0',
      overflowY: 'auto',
      boxSizing: 'border-box',
      width: '100%',
      height: '100%',
      background: 'rgba(7,22,27,0.55)',
      border: `${Math.max(1, Math.round(s))}px solid rgba(143,208,224,0.35)`,
    });
    box.dataset.list = '1';
    // a11y: the pack catalog ListControl → role=listbox; its rows are role=option. Net-new.
    box.setAttribute('role', 'listbox');
    box.setAttribute('aria-label', 'Mission packs');
    if (rows.length === 0) {
      box.style.alignItems = 'center';
      box.style.justifyContent = 'center';
      const note = el('div', {
        ...labelStyle(s),
        opacity: '0.7',
        fontStyle: 'italic',
        textAlign: 'center',
        padding: `0 ${4 * s}px`,
      });
      note.textContent = emptyMsg;
      box.append(note);
    } else {
      for (const r of rows)
        box.append(this.listItem(r.selected, r.onSelect, r.onActivate, s, r.html));
    }
    return box;
  }

  // a selectable scrolling-list row (the canvas ListControl item): the SELECTED
  // row a SOLID steel-blue bar with dark text (kiclrButtonFillHighlight) — not a
  // faint tint — click-to-select (with the select SFX) + double-click to
  // activate. Copied from lobby-view.ts.
  private listItem(
    selected: boolean,
    onSelect: () => void,
    onActivate: () => void,
    s: number,
    content: HTMLElement,
  ): HTMLElement {
    const item = el('button', {
      textAlign: 'left',
      padding: `${1.5 * s}px ${4 * s}px`,
      background: selected ? 'rgb(122, 168, 198)' : 'transparent',
      color: selected ? '#0c1a22' : COL_FG,
      border: 'none',
      cursor: 'pointer',
      font: 'inherit',
      fontFamily: FONT,
      fontSize: `${Math.round(5 * s)}px`,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      flex: '0 0 auto',
      textShadow: selected ? 'none' : shadow,
    });
    content.style.color = selected ? '#0c1a22' : COL_FG;
    item.append(content);
    // a11y: each catalog row → role=option + aria-selected (net-new, visual-neutral).
    item.setAttribute('role', 'option');
    item.setAttribute('aria-selected', selected ? 'true' : 'false');
    item.addEventListener('mouseenter', () => {
      if (!selected) item.style.background = 'rgba(143,208,224,0.14)';
    });
    item.addEventListener('mouseleave', () => {
      if (!selected) item.style.background = 'transparent';
    });
    item.addEventListener('click', () => {
      this.d.playSfx?.(ksfxGuiScrollingListSelectItem);
      onSelect();
    });
    item.addEventListener('dblclick', onActivate);
    return item;
  }
}

function labelStyle(s: number): Partial<CSSStyleDeclaration> {
  return {
    fontFamily: FONT,
    fontSize: `${Math.round(5.5 * s)}px`,
    color: COL_FG,
    textShadow: shadow,
  };
}
